<div class="employees-page">
  <div class="container py-3">
    <div class="page-header d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
      <div>
        <h2 class="page-title mb-1">Employees</h2>
        <p class="page-subtitle mb-0">
          Staff records, ID expiry, and leave balances.
          New logins are added from <a routerLink="/users">Users</a>.
          Hide people from <a routerLink="/staff-settings">Settings</a>.
        </p>
      </div>
      <div class="header-actions d-flex flex-wrap gap-2">
        <a class="btn btn-outline-secondary" routerLink="/attendance">Leave requests</a>
        <a class="btn btn-outline-secondary" routerLink="/employee-evaluations">Evaluations</a>
        <button type="button" class="btn btn-outline-secondary" (click)="exportCsv()" [disabled]="filteredItems.length === 0">
          Export
        </button>
        <button type="button" class="btn btn-primary btn-add" (click)="load()">Refresh</button>
      </div>
    </div>

    @if (focusedUserId) {
      <div class="alert alert-info d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Showing the employee linked to this user account.</span>
        <button type="button" class="btn btn-sm btn-outline-primary" (click)="clearFocus()">Show everyone</button>
      </div>
    }

    @if (loadError) {
      <div class="alert alert-danger d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Could not load employees.</span>
        <button type="button" class="btn btn-sm btn-outline-danger" (click)="load()">Try again</button>
      </div>
    }

    <div class="row g-3 mb-4">
      <div class="col-6 col-lg-3">
        <button type="button" class="stat-card" [class.active]="isQuick('all')" (click)="applyQuick('all')">
          <span class="stat-label">Employees</span>
          <span class="stat-value">{{ counts.all }}</span>
        </button>
      </div>
      <div class="col-6 col-lg-3">
        <button type="button" class="stat-card" [class.active]="isQuick('active')" (click)="applyQuick('active')">
          <span class="stat-label">Active</span>
          <span class="stat-value">{{ counts.active }}</span>
        </button>
      </div>
      <div class="col-6 col-lg-3">
        <button type="button" class="stat-card" [class.active]="isQuick('leave')" (click)="applyQuick('leave')">
          <span class="stat-label">On leave</span>
          <span class="stat-value">{{ counts.leave }}</span>
        </button>
      </div>
      <div class="col-6 col-lg-3">
        <button type="button" class="stat-card" [class.active]="isQuick('id')" (click)="applyQuick('id')">
          <span class="stat-label">ID needs attention</span>
          <span class="stat-value">{{ counts.idAlert }}</span>
        </button>
      </div>
    </div>

    <div class="card filter-card mb-4">
      <div class="filter-bar">
        <button
          type="button"
          class="filter-toggle"
          (click)="filtersOpen = !filtersOpen"
          [attr.aria-expanded]="filtersOpen"
          aria-controls="employee-filters"
        >
          <span class="filter-chevron" [class.open]="filtersOpen" aria-hidden="true"></span>
          Filters
          @if (activeFilterCount > 0) {
            <span class="filter-count">{{ activeFilterCount }}</span>
          }
        </button>
        @if (!filtersOpen && filterSummary) {
          <span class="filter-summary">{{ filterSummary }}</span>
        }
        @if (activeFilterCount > 0) {
          <button type="button" class="btn btn-link btn-sm filter-reset" (click)="resetFilters()">Reset</button>
        }
      </div>
      @if (filtersOpen) {
      <div class="card-body py-3 border-top" id="employee-filters">
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label small text-muted mb-1" for="employee-search">Search</label>
            <input
              id="employee-search"
              type="search"
              class="form-control"
              placeholder="Name, phone, ID, email..."
              [(ngModel)]="searchTerm"
              (ngModelChange)="onFilterChange()"
            />
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small text-muted mb-1" for="employee-role">Role</label>
            <select id="employee-role" class="form-select" [(ngModel)]="roleFilter" (ngModelChange)="onFilterChange()">
              <option value="all">All</option>
              @for (role of roleOptions; track role) {
                <option [value]="role">{{ role | titlecase }}</option>
              }
            </select>
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small text-muted mb-1" for="employee-status">Status</label>
            <select id="employee-status" class="form-select" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()">
              <option value="all">All</option>
              @for (status of statusChoices; track status.value) {
                <option [value]="status.value">{{ status.label }}</option>
              }
            </select>
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small text-muted mb-1" for="employee-id">ID expiry</label>
            <select id="employee-id" class="form-select" [(ngModel)]="idFilter" (ngModelChange)="onFilterChange()">
              <option value="all">All</option>
              <option value="attention">Expiring or expired</option>
              <option value="expiring">Within 60 days</option>
              <option value="expired">Expired</option>
              <option value="missing">No expiry date</option>
            </select>
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small text-muted mb-1" for="employee-account">Login</label>
            <select id="employee-account" class="form-select" [(ngModel)]="accountFilter" (ngModelChange)="onFilterChange()">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div class="col-6 col-md-3">
            <label class="form-label small text-muted mb-1" for="employee-sort">Sort</label>
            <select id="employee-sort" class="form-select" [(ngModel)]="sortBy" (ngModelChange)="onFilterChange()">
              <option value="name">Name</option>
              <option value="salary">Salary, high to low</option>
              <option value="expiry">ID expiry, soonest</option>
              <option value="leave">Annual balance, lowest</option>
            </select>
          </div>
          <div class="col-6 col-md-2">
            <button type="button" class="btn btn-outline-secondary w-100" (click)="resetFilters()">Reset</button>
          </div>
        </div>
      </div>
      }
    </div>

    <div class="card table-card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span>Staff directory</span>
        @if (filteredItems.length > 0) {
          <span class="badge rounded-pill bg-light text-dark">{{ filteredItems.length }} employee(s)</span>
        }
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover employee-table mb-0">
            <thead>
              <tr>
                <th scope="col" class="col-num">#</th>
                <th scope="col">Employee</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">ID</th>
                <th scope="col">Salary</th>
                <th scope="col">Leave</th>
                <th scope="col" class="text-end col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (filteredItems.length === 0) {
                <tr>
                  <td colspan="8" class="empty-state">
                    <div class="empty-content">
                      @if (loadError) {
                        <p class="mb-1 fw-semibold">Employees could not be loaded</p>
                        <p class="text-muted small mb-0">Check the connection, then try again.</p>
                      } @else {
                        <p class="mb-1 fw-semibold">No employees found</p>
                        <p class="text-muted small mb-0">Try another search, or reset the filters.</p>
                      }
                    </div>
                  </td>
                </tr>
              }
              @for (item of paginatedItems; track item.id; let i = $index) {
                <tr [class.row-expired]="item.idState === 'expired'" [class.row-expiring]="item.idState === 'expiring'">
                  <td class="text-muted">{{ (page - 1) * pageSize + i + 1 }}</td>
                  <td>
                    <button type="button" class="name-button" (click)="openDetails(detailsModal, item)">
                      {{ item.name }}
                    </button>
                    @if (isAccountDisabled(item)) {
                      <span class="badge bg-secondary ms-1">Login off</span>
                    }
                    <div class="subline">
                      {{ item.username || 'No username' }}
                      @if (item.phone) {
                        <span> · {{ item.phone }}</span>
                      }
                    </div>
                    @if (item.country) {
                      <div class="subline">{{ item.country }}</div>
                    }
                  </td>
                  <td>
                    @if (item.role) {
                      <span class="badge" [ngClass]="roleBadge(item.role)">{{ item.role | titlecase }}</span>
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    <span class="badge" [ngClass]="statusMeta(item.status).badge">{{ statusMeta(item.status).label }}</span>
                    @if (item.status === 'LEAVE' && (item.status_from_date || item.status_to_date)) {
                      <div class="subline">
                        {{ displayDate(item.status_from_date) }} – {{ displayDate(item.status_to_date) }}
                      </div>
                    }
                  </td>
                  <td>
                    <div>{{ item.id_number || '—' }}</div>
                    @if (item.id_expiry_date) {
                      <div
                        class="small"
                        [class.id-expired]="item.idState === 'expired'"
                        [class.id-expiring]="item.idState === 'expiring'"
                        [class.text-muted]="item.idState === 'ok'"
                      >
                        {{ displayDate(item.id_expiry_date) }}
                      </div>
                    }
                    @if (expiryText(item); as note) {
                      <div class="small" [class.id-expired]="item.idState === 'expired'" [class.id-expiring]="item.idState === 'expiring'">
                        {{ note }}
                      </div>
                    }
                  </td>
                  <td class="text-nowrap">
                    @if (item.basic_salary != null && item.basic_salary !== '') {
                      OMR {{ amount(item.basic_salary) | number:'1.3-3' }}
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    <div class="leave-pills">
                      <span title="Annual leave balance">Annual {{ displayAmount(item.leave_balance) }}</span>
                      <span title="Sick leave balance">Sick {{ displayAmount(item.sick_balance) }}</span>
                      @if (amount(item.hours_balance) !== 0) {
                        <span title="Hours balance">Hours {{ displayAmount(item.hours_balance) }}</span>
                      }
                    </div>
                  </td>
                  <td class="text-end">
                    <div class="d-flex flex-nowrap gap-1 justify-content-end">
                      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openEdit(editModal, item)">Edit</button>
                      <button type="button" class="btn btn-sm btn-outline-dark" (click)="openStatus(statusModal, item)">Status</button>
                      <button type="button" class="btn btn-sm btn-outline-success" title="Reset annual leave to 30 and sick leave to 10" (click)="resetBalance(item)">Balance</button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      @if (filteredItems.length > pageSize) {
        <div class="card-footer bg-white border-top-0 pagination-footer">
          <ngb-pagination
            class="mb-0"
            [collectionSize]="filteredItems.length"
            [(page)]="page"
            [pageSize]="pageSize"
            [maxSize]="5"
            [rotate]="true"
            [boundaryLinks]="true"
          ></ngb-pagination>
        </div>
      }
    </div>
  </div>
</div>

<ng-template #detailsModal let-modal>
  @if (selected) {
    <div class="modal-header">
      <div>
        <h4 class="modal-title mb-1">{{ selected.name }}</h4>
        <p class="text-muted small mb-0">{{ selected.username || 'No username' }}</p>
      </div>
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <div class="row g-3">
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Account</h6>
            <div class="detail-row"><span>Role</span><span>{{ selected.role ? (selected.role | titlecase) : '—' }}</span></div>
            <div class="detail-row"><span>Email</span><span>{{ selected.email || '—' }}</span></div>
            <div class="detail-row"><span>Phone</span><span>{{ selected.phone || '—' }}</span></div>
            <div class="detail-row">
              <span>Login</span>
              <span>{{ isAccountDisabled(selected) ? 'Disabled' : (isAccountActive(selected) ? 'Active' : '—') }}</span>
            </div>
            <p class="small text-muted mb-0 mt-2">Role and login are changed from <a routerLink="/users" (click)="modal.dismiss()">Users</a>.</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Personal</h6>
            <div class="detail-row"><span>Gender</span><span>{{ genderLabel(selected.gender) }}</span></div>
            <div class="detail-row"><span>Date of birth</span><span>{{ displayDate(selected.birth_date) }}</span></div>
            <div class="detail-row"><span>Nationality</span><span>{{ selected.country || '—' }}</span></div>
            <div class="detail-row"><span>Address</span><span>{{ selected.address || '—' }}</span></div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Identity card</h6>
            <div class="detail-row"><span>ID number</span><span>{{ selected.id_number || '—' }}</span></div>
            <div class="detail-row"><span>Expiry</span><span>{{ displayDate(selected.id_expiry_date) }}</span></div>
            @if (expiryText(selected); as note) {
              <p class="small mb-2 mt-2" [class.id-expired]="selected.idState === 'expired'" [class.id-expiring]="selected.idState === 'expiring'">{{ note }}</p>
            }
            @if (attachmentUrl(selected.id_attachment); as fileUrl) {
              <a class="file-link" [href]="fileUrl" target="_blank" rel="noopener">Open ID copy</a>
            } @else {
              <p class="small text-muted mb-0 mt-2">No ID copy on file.</p>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Pay</h6>
            <div class="detail-row">
              <span>Basic salary</span>
              <span>
                @if (selected.basic_salary != null && selected.basic_salary !== '') {
                  OMR {{ amount(selected.basic_salary) | number:'1.3-3' }}
                } @else {
                  —
                }
              </span>
            </div>
            <div class="detail-row"><span>Bank</span><span>{{ selected.bank_name || '—' }}</span></div>
            <div class="detail-row"><span>Account</span><span>{{ selected.account_number || '—' }}</span></div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Leave balances</h6>
            <div class="detail-row"><span>Annual</span><span>{{ displayAmount(selected.leave_balance) }}</span></div>
            <div class="detail-row"><span>Sick</span><span>{{ displayAmount(selected.sick_balance) }}</span></div>
            <div class="detail-row"><span>Hours</span><span>{{ displayAmount(selected.hours_balance) }}</span></div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="detail-card">
            <h6>Employment</h6>
            <div class="detail-row"><span>Status</span><span>{{ statusMeta(selected.status).label }}</span></div>
            @if (selected.status === 'LEAVE') {
              <div class="detail-row"><span>From</span><span>{{ displayDate(selected.status_from_date) }}</span></div>
              <div class="detail-row"><span>To</span><span>{{ displayDate(selected.status_to_date) }}</span></div>
            }
            <div class="detail-row"><span>Updated</span><span>{{ selected.updated_at ? (selected.updated_at | date:'dd/MM/yyyy') : '—' }}</span></div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-secondary" (click)="editFromDetails()">Edit</button>
      <button type="button" class="btn btn-outline-dark" (click)="statusFromDetails()">Update status</button>
      <button type="button" class="btn btn-primary btn-add" (click)="modal.close()">Close</button>
    </div>
  }
</ng-template>

<ng-template #editModal let-modal>
  <div class="modal-header">
    <div>
      <h4 class="modal-title mb-1">Edit employee</h4>
      <p class="text-muted small mb-0">{{ selected?.name }}</p>
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label" for="edit-name">Name</label>
        <input id="edit-name" type="text" class="form-control" [(ngModel)]="form.name" />
      </div>
      <div class="col-md-3">
        <label class="form-label" for="edit-gender">Gender</label>
        <select id="edit-gender" class="form-select" [(ngModel)]="form.gender">
          <option value="0">Select</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          @if (form.gender && form.gender !== '0' && form.gender !== 'male' && form.gender !== 'female') {
            <option [value]="form.gender">{{ form.gender }}</option>
          }
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label" for="edit-country">Nationality</label>
        <input id="edit-country" type="text" class="form-control" [(ngModel)]="form.country" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-phone">Phone</label>
        <input id="edit-phone" type="text" class="form-control" [(ngModel)]="form.phone" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-email">Email</label>
        <input id="edit-email" type="email" class="form-control" [(ngModel)]="form.email" />
      </div>
      <div class="col-12">
        <label class="form-label" for="edit-address">Address</label>
        <input id="edit-address" type="text" class="form-control" [(ngModel)]="form.address" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-birth">Date of birth</label>
        <input id="edit-birth" type="date" class="form-control" [(ngModel)]="form.birth_date" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-salary">Basic salary (OMR)</label>
        <input id="edit-salary" type="number" min="0" step="0.001" class="form-control" [(ngModel)]="form.basic_salary" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-id-number">ID number</label>
        <input id="edit-id-number" type="text" class="form-control" [(ngModel)]="form.id_number" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-id-expiry">ID expiry</label>
        <input id="edit-id-expiry" type="date" class="form-control" [(ngModel)]="form.id_expiry_date" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-bank">Bank</label>
        <input id="edit-bank" type="text" class="form-control" [(ngModel)]="form.bank_name" />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="edit-account">Account number</label>
        <input id="edit-account" type="text" class="form-control" [(ngModel)]="form.account_number" />
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-primary btn-add" (click)="saveEmployee()" [disabled]="saving">
      {{ saving ? 'Saving...' : 'Save' }}
    </button>
  </div>
</ng-template>

<ng-template #statusModal let-modal>
  <div class="modal-header">
    <div>
      <h4 class="modal-title mb-1">Update status</h4>
      <p class="text-muted small mb-0">{{ selected?.name }}</p>
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="mb-3">
      <label class="form-label" for="status-value">Status</label>
      <select id="status-value" class="form-select" [(ngModel)]="statusForm.status">
        <option value="">Select</option>
        @for (status of statusChoices; track status.value) {
          <option [value]="status.value">{{ status.label }}</option>
        }
      </select>
    </div>
    @if (statusForm.status === 'LEAVE') {
      <div class="row g-3">
        <div class="col-md-6">
          <label class="form-label" for="status-from">From</label>
          <input id="status-from" type="date" class="form-control" [(ngModel)]="statusForm.status_from_date" />
        </div>
        <div class="col-md-6">
          <label class="form-label" for="status-to">To</label>
          <input id="status-to" type="date" class="form-control" [(ngModel)]="statusForm.status_to_date" />
        </div>
      </div>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-primary btn-add" (click)="saveStatus()" [disabled]="saving">
      {{ saving ? 'Saving...' : 'Update' }}
    </button>
  </div>
</ng-template>
